import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import bannersModule, { BannerTypes } from '@gin-betting/banners';
import core, { App, GQL, View } from '@gin-betting/core';
import { groupBy } from 'lodash';
import { createSelector } from 'reselect';

import { ACTIVE_MARKET_STATUSES } from 'app-constants';

interface Input {
    zoneId: string;
}

interface Output {
    sports: View.Sport[];
    sportEventIds: string[];
    activeSportId: string;
    isLoadingTimedOut: boolean;
    fetchStatus: App.FetchStatus | undefined;
    handleTabChange: (sportId: string) => void;
    preloadMatches: (sportId: string) => void;
}

const {
    actions: { getBannersData, getMissingMatches },
    selectors: { makeBannerItemsSelector, makeSelectBannerByZoneId },
} = bannersModule;

const {
    poller: { usePoller },
    entities: {
        selectors: { selectSports, selectMissingSportEventIds },
    },
    hooks: { useDeepMemo },
    utils: { getBettingOptions },
} = core;

const COMMON_GET_MATCHES_VARS: Omit<
    GQL.GetMatchesByIdsQueryVariables,
    'sportEventsIds'
> = {
    isTopMarkets: true,
    marketLimit: 1,
    withMarketsCount: true,
    marketStatuses: ACTIVE_MARKET_STATUSES,
};

const preloadedSportsCache: string[] = [];

function useTopMatch({ zoneId }: Input): Output {
    const dispatch = useDispatch();
    const [activeSportId, setActiveSportId] = useState<string>('');
    const [isLoadingTimedOut, setLoadingTimedOut] = useState(false);

    const selectBannerZoneById = useMemo(
        () => makeSelectBannerByZoneId(zoneId),
        [zoneId]
    );

    const fetchStatus = useSelector(selectBannerZoneById)?.fetchStatus;

    const { sports, bannerItemsBySport } = useSelector(
        makeBannerItemsAndSportsSelector(
            makeBannerItemsSelector(
                zoneId,
                GQL.BannerType.betting_sport_event,
                selectBannerZoneById
            )
        )
    );

    const bannerItems = useDeepMemo(bannerItemsBySport);
    const sportEventIds = bannerItems[activeSportId]?.map(({ id }) => id) || [];

    const missingMatchIds = useDeepMemo(
        useSelector(state => {
            return selectMissingSportEventIds(state, {
                sportEventIds,
            });
        })
    );

    const handleTabChange = useCallback((sportId: string) => {
        setActiveSportId(sportId);
    }, []);

    const preloadMatches = useCallback(
        (sportId: string) => {
            if (preloadedSportsCache.includes(sportId)) return;

            preloadedSportsCache.push(sportId);

            const preloadSportEventIds = bannerItems[sportId]?.map(
                ({ id }) => id
            );

            if (!preloadSportEventIds) return;

            dispatch(
                getMissingMatches({
                    sportEventsIds: preloadSportEventIds,
                    ...COMMON_GET_MATCHES_VARS,
                })
            );
        },
        [bannerItems, dispatch]
    );

    useEffect(() => {
        if (missingMatchIds.length === 0) return;

        dispatch(
            getMissingMatches({
                sportEventsIds: missingMatchIds,
                ...COMMON_GET_MATCHES_VARS,
            })
        );
    }, [missingMatchIds, dispatch]);

    const { requestContent } = usePoller({
        pollerName: 'BANNER_TOP_SPORT_EVENTS',
        executableAction: getBannersData,
        variables: {
            zoneId,
            marketStatuses: ACTIVE_MARKET_STATUSES,
            withSportEvent: false,
            sportIds: getBettingOptions().sportIds,
        },
        isPollingTurnedOff: false,
    });

    useEffect(() => {
        requestContent();
    }, [zoneId, requestContent]);

    useEffect(() => {
        const firstSportId = sports[0]?.id;

        if (
            !firstSportId ||
            (activeSportId && sports.find(({ id }) => id === activeSportId))
        ) {
            return;
        }

        setActiveSportId(firstSportId);
    }, [activeSportId, sports]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setLoadingTimedOut(true);
        }, 5000);

        return () => {
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
        };
    }, []);

    return {
        sports,
        sportEventIds,
        activeSportId,
        handleTabChange,
        preloadMatches,
        fetchStatus,
        isLoadingTimedOut,
    };
}

function makeBannerItemsAndSportsSelector(
    selectSportEventBannerItems: (
        ...args: any[]
    ) => BannerTypes.BannerSportEvent[]
) {
    return createSelector(
        selectSportEventBannerItems,
        selectSports,
        (bannerSportEvents, allSports) => {
            const bannerItemsBySport = groupBy(
                bannerSportEvents,
                item => item.group?.groupBy
            );

            const sports = Object.keys(bannerItemsBySport)
                .map(sportId => allSports.get(sportId))
                .filter((v): v is View.Sport => !!v);

            return {
                bannerItemsBySport,
                sports,
            };
        }
    );
}

export default useTopMatch;
